import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import {
  getRegulationAssessmentPacks,
  type AssessmentPack,
} from "../../../services/assessmentPackService";
import { AssessmentPackCard } from "./AssessmentPackCard";

type LoadState = "success" | "error";

interface PackLoadResult {
  key: string;
  state: LoadState;
  packs: AssessmentPack[];
  errorMessage: string | null;
}

const EMPTY_RESULT: PackLoadResult = {
  key: "",
  state: "success",
  packs: [],
  errorMessage: null,
};

export const AssessmentPackSection: React.FC<{
  regulationId?: string;
  version?: string;
}> = ({ regulationId = "", version = "" }) => {
  const [retryToken, setRetryToken] = useState(0);
  const [result, setResult] = useState<PackLoadResult>(EMPTY_RESULT);

  const canFetch = Boolean(regulationId.trim() && version.trim());
  const requestKey = canFetch
    ? `${regulationId}\n${version}\n${retryToken}`
    : "";
  const settled = canFetch && result.key === requestKey;

  useEffect(() => {
    if (!canFetch) return;

    let cancelled = false;
    const key = `${regulationId}\n${version}\n${retryToken}`;

    void getRegulationAssessmentPacks(regulationId, version)
      .then((res) => {
        if (cancelled) return;
        if (res.status === "error") {
          setResult({
            key,
            state: "error",
            packs: [],
            errorMessage:
              res.message || "Failed to load the impact assessment package.",
          });
          return;
        }
        setResult({
          key,
          state: "success",
          packs: res.data ?? [],
          errorMessage: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setResult({
          key,
          state: "error",
          packs: [],
          errorMessage:
            err instanceof Error
              ? err.message
              : "Failed to load the impact assessment package.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [canFetch, regulationId, version, retryToken]);

  if (!canFetch) return null;

  if (!settled) {
    return (
      <Box
        role="status"
        aria-live="polite"
        aria-label="Loading impact assessment package"
        sx={{ display: "flex", justifyContent: "center", py: 3, mb: 2 }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (result.state === "error") {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => setRetryToken((token) => token + 1)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Try Again
          </Button>
        }
      >
        {result.errorMessage || "Failed to load the impact assessment package."}
      </Alert>
    );
  }

  if (result.packs.length === 0) {
    return (
      <Typography
        role="status"
        sx={{ color: "#6A7282", fontSize: 13, py: 2, mb: 1 }}
      >
        No impact assessment package is available for this insight.
      </Typography>
    );
  }

  return (
    <Box
      role="list"
      aria-label="Impact assessment packages"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "stretch",
        mb: 2.5,
      }}
    >
      {result.packs.map((pack) => (
        <Box
          key={pack.id}
          role="listitem"
          sx={{ width: 370, maxWidth: "100%", display: "flex" }}
        >
          <AssessmentPackCard pack={pack} />
        </Box>
      ))}
    </Box>
  );
};
